import React, { useState, useEffect } from 'react';
import { Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { tournamentScheduleAPI } from '../services/tournamentApi';
import GameComponent from '../../scoring/GameComponent';
import { apiCall } from '../../services/apiUtil';
import toast from 'react-hot-toast';

export default function LiveGameTab({
  gameId,
  gameCommonId: gameCommonIdProp = null,
  sportName,
  teamsData,
  onClose,
  onGameComplete,
}) {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [gameCommonId, setGameCommonId] = useState(gameCommonIdProp);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (gameId) {
      loadGame();
    }
  }, [gameId]);

  const loadGame = async () => {
    if (!gameId) {
      setError('Game ID is missing');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const gameData = await tournamentScheduleAPI.getGame(gameId);
      setGame(gameData);

      // Only look up game_common if we weren't given one (e.g. View button on completed game)
      if (!gameCommonIdProp && gameData.event?.id) {
        await checkForExistingGameCommon(gameData.event.id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load game');
      toast.error('Failed to load game details');
    } finally {
      setLoading(false);
    }
  };

  // Fallback: look up game_common by event_id (used when opening a completed game to view)
  const checkForExistingGameCommon = async (eventId) => {
    try {
      const data = await apiCall(`/game-common/by-event/${eventId}`);
      if (data && data.id) {
        setGameCommonId(data.id);
      }
    } catch (err) {
      // No game_common yet — GameComponent will handle creation
    }
  };

  const handleGameComplete = async (result) => {
    // Pass result to parent - parent will handle recording
    try {
      await onGameComplete?.(gameId, result);
    } catch (err) {
      console.error('Error completing game:', err);
      toast.error(err.message || 'Failed to complete game');
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${theme.background}`}>
        <Loader className={`w-8 h-8 animate-spin ${theme.textMuted}`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center h-64 ${theme.background}`}>
        <p className={`${theme.text} mb-4`}>{error}</p>
        <button
          onClick={loadGame}
          className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!game) {
    return (
      <div className={`flex items-center justify-center h-64 ${theme.background}`}>
        <p className={theme.textMuted}>Game not found</p>
      </div>
    );
  }

  // Get sport type from game data
  const sportType = sportName || game.sport_name;

  // Get sportTypeId from tournament details
  const sportTypeId = game.tournament?.sport_type_id || null;
  const maxPlayersPerSide = game.tournament?.sport_typea?.max_players_per_side ?? null;


  // Validate that game has an associated event
  if (!game.event) {
    return (
      <div className={`flex flex-col items-center justify-center h-64 ${theme.background}`}>
        <p className={`${theme.text} text-lg mb-2`}>Event Not Found</p>
        <p className={theme.textMuted}>
          This tournament game doesn't have an associated event. Please ensure the tournament schedule was created properly.
        </p>
      </div>
    );
  }

  // Use the real event from the tournament game
  const event = game.event;

  // Use teams data from props if available, otherwise create empty array
  const members = teamsData?.members || [];
  const initialTeams = teamsData?.initialTeams || undefined;

  // Extract game settings — a per-match override (set via the schedule
  // editor for this specific match) takes priority over the tournament's
  // own match_configuration, when present. The override is a native jsonb
  // column (already a parsed object), unlike match_configuration which is
  // stored as a JSON string.
  let gameSettings = {};
  const hasMatchSettingsOverride = game.overridden_match_settings && Object.keys(game.overridden_match_settings).length > 0;
  if (hasMatchSettingsOverride) {
    gameSettings = { ...game.overridden_match_settings };
  } else if (game.tournament?.match_configuration) {
    try {
      gameSettings = typeof game.tournament.match_configuration === 'string'
        ? JSON.parse(game.tournament.match_configuration)
        : game.tournament.match_configuration;
    } catch (err) {
      console.error('Failed to parse match_configuration:', err);
    }
  }

  // Always set participantsType from tournament.participants_type as source
  // of truth, overriding anything stored in match_configuration — but only
  // when there's no per-match override. An override that deliberately picks
  // a different participantsType (e.g. one match played singles even though
  // the tournament defaults to doubles) needs to actually take effect, not
  // get silently stomped back to the tournament's own setting.
  const tournamentParticipantsType = game.tournament?.participants_type;
  if (tournamentParticipantsType && !hasMatchSettingsOverride) {
    gameSettings = { ...gameSettings, participantsType: tournamentParticipantsType };
  }

  // Build participantInfo. A stored per-match player subset (set via the
  // schedule editor when a team's roster exceeds what the sport format plays,
  // e.g. doubles = 2 picked from an 8-player team) takes priority over
  // initialTeams/participant_details, which otherwise carry the team's full
  // roster — this is what lets GameComponent already know who's playing
  // without any changes on its side.
  const participantAInfo = (game.participant_a_players?.length > 0 ? {
    type: game.participant_a_type,
    id: game.participant_a_id,
    name: game.participant_a_name || 'Team A',
    players: game.participant_a_players
  } : null) || (initialTeams?.[0] ? {
    type: game.participant_a_type,
    id: game.participant_a_id,
    name: initialTeams[0].name,
    players: initialTeams[0].players
  } : null) || game.participant_a_details || (game.participant_a_id ? {
    type: game.participant_a_type,
    id: game.participant_a_id,
    name: game.participant_a_name || 'Team A'
  } : null);

  const participantBInfo = (game.participant_b_players?.length > 0 ? {
    type: game.participant_b_type,
    id: game.participant_b_id,
    name: game.participant_b_name || 'Team B',
    players: game.participant_b_players
  } : null) || (initialTeams?.[1] ? {
    type: game.participant_b_type,
    id: game.participant_b_id,
    name: initialTeams[1].name,
    players: initialTeams[1].players
  } : null) || game.participant_b_details || (game.participant_b_id ? {
    type: game.participant_b_type,
    id: game.participant_b_id,
    name: game.participant_b_name || 'Team B'
  } : null);

  // Full team rosters, regardless of any per-match player subset narrowing
  // above — needed for a mid-match player swap (e.g. an MLP Dreambreaker
  // played as singles by one of a team's 4 players, but any of the 4 could
  // sub in). participantAInfo/BInfo above intentionally narrow to just the
  // currently-assigned player(s); these are the same team's *entire* roster,
  // for debug verification now and the swap UI later.
  const participantATeamRoster = initialTeams?.[0]?.players || [];
  const participantBTeamRoster = initialTeams?.[1]?.players || [];


  return (
    <div className={`${theme.background} min-h-screen`}>
      <GameComponent
        sportType={sportType}
        sportTypeId={sportTypeId}
        maxPlayersPerSide={maxPlayersPerSide}
        gameCommonId={gameCommonId}
        groupId={null}  // Not used for tournaments
        members={members}
        event={event}
        onGameComplete={handleGameComplete}
        onCancel={onClose}
        backLabel="Back to Schedule"
        // Tournament-specific: pre-populate with known participants and settings
        initialParticipantA={participantAInfo}
        initialParticipantB={participantBInfo}
        // Full team rosters (unnarrowed) — temp debug verification + future swap UI
        participantATeamRoster={participantATeamRoster}
        participantBTeamRoster={participantBTeamRoster}
        initialGameSettings={gameSettings}
      />
    </div>
  );
}
